import {
  BadgeColorScheme,
  SubmitHandler,
  useForm,
  UseFormReturn,
} from '@agrotoken-tech/ui';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import {
  CommonCropPledgeDocumentSerializer,
  CommonCropPledgeDocumentsSerializer,
} from '@bcba/serializers';
import {
  CROP_PLEDGE_USER_ROLES,
  CropPledgeUserRole,
  PLEDGE_SECTION_STATUSES,
  PledgeSectionStatus,
} from '@bcba/types';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  getCropPledgeDocumentation,
  patchCropPledgeDocumentation,
} from '../../../../../services/crop-pledge';
import { ExplorerUpsertCropPledgeDocumentationDto } from '../../../../../services/crop-pledge/dto/update';
import { useCropPledgeContext } from '../../context/CropPledgeContext';

const resolver = classValidatorResolver(
  ExplorerUpsertCropPledgeDocumentationDto,
);

type CropPledgeDocumentationContextType = {
  data?: CommonCropPledgeDocumentsSerializer;
  isLoading: boolean;
  refetch: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<CommonCropPledgeDocumentsSerializer, Error>>;
  cropPledgeSectionsStatusBadgeColors: Record<
    (typeof PLEDGE_SECTION_STATUSES)[keyof typeof PLEDGE_SECTION_STATUSES],
    BadgeColorScheme
  >;
  role: CropPledgeUserRole;
  documentationStatus: PledgeSectionStatus;
  formMethods: UseFormReturn<
    ExplorerUpsertCropPledgeDocumentationDto,
    any,
    undefined
  >;
  onSubmit: SubmitHandler<ExplorerUpsertCropPledgeDocumentationDto>;
  isPendingRequestApproval: boolean;
  isFormReadOnly: boolean;
};

const CropPledgeDocumentationContext = createContext<
  CropPledgeDocumentationContextType | undefined
>(undefined);
type CropPledgeDocumentationProviderProps = {
  children: ReactNode;
};

const mapCropPledgeDocumentToForm = (
  doc?: CommonCropPledgeDocumentSerializer,
) => ({
  uuid: doc?.uuid,
  documentUuid: doc?.document?.uuid,
  keepOriginal: Boolean(doc?.uuid),
  originalName: doc?.document?.originalFileName,
  identifier: doc?.document?.identifier,
  expirationDate: doc?.document?.expirationDate,
});

const mapCropPledgeDocumentationSerializerToForm = (
  data?: CommonCropPledgeDocumentsSerializer,
): ExplorerUpsertCropPledgeDocumentationDto => {
  return {
    loanContractFile: mapCropPledgeDocumentToForm(data?.loanContractFile),
    additionalDocumentFiles: data?.additionalDocumentFiles?.length
      ? data.additionalDocumentFiles.map(mapCropPledgeDocumentToForm)
      : [mapCropPledgeDocumentToForm()],
  };
};

export const CropPledgeDocumentationProvider: React.FC<
  CropPledgeDocumentationProviderProps
> = ({ children }) => {
  const { t } = useTranslation();
  const params = useParams();
  const cropPledgeUuid = params['uuid'] || '';
  const {
    cropPledgeSectionsStatusBadgeColors,
    role,
    refetchCropPledgeStatus,
    isPledgeInAnEditableStage,
  } = useCropPledgeContext();
  const { showSuccessToast } = useToastsHook();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['get-crop-pledge-documentation', cropPledgeUuid],
    queryFn: () => getCropPledgeDocumentation(cropPledgeUuid),
  });

  const formMethods = useForm<any>({
    resolver,
    defaultValues: mapCropPledgeDocumentationSerializerToForm(data),
  });

  const { reset } = formMethods;

  useEffect(() => {
    reset(mapCropPledgeDocumentationSerializerToForm(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { mutateAsync: requestApproval, isPending: isPendingRequestApproval } =
    useMutation({
      mutationFn: (payload: ExplorerUpsertCropPledgeDocumentationDto) => {
        return patchCropPledgeDocumentation(cropPledgeUuid, payload);
      },
      onSuccess: () => {
        refetch();
        refetchCropPledgeStatus();
        showSuccessToast({ title: t('common.requestApproval.toast.success') });
      },
    });

  const onSubmit: SubmitHandler<ExplorerUpsertCropPledgeDocumentationDto> = (
    data,
  ) => {
    if (role === CROP_PLEDGE_USER_ROLES.DEBTOR) {
      const { loanContractFile, additionalDocumentFiles } = data;

      (loanContractFile as any).documentUuid = undefined;

      if (additionalDocumentFiles) {
        additionalDocumentFiles.forEach((doc: any) => {
          doc.documentUuid = undefined;
        });
      }

      requestApproval(data);
    }
  };

  const documentationStatus = (data?.status ??
    PLEDGE_SECTION_STATUSES.PENDING_DEBTOR_LOAD) as PledgeSectionStatus;

  const isFormReadOnly =
    !isPledgeInAnEditableStage ||
    role === CROP_PLEDGE_USER_ROLES.CREDITOR ||
    (role === CROP_PLEDGE_USER_ROLES.DEBTOR &&
      documentationStatus !== PLEDGE_SECTION_STATUSES.PENDING_DEBTOR_LOAD &&
      documentationStatus !== PLEDGE_SECTION_STATUSES.PENDING_BOLSA_LOAD);

  return (
    <CropPledgeDocumentationContext.Provider
      value={{
        data,
        isLoading,
        refetch,
        cropPledgeSectionsStatusBadgeColors,
        role,
        documentationStatus,
        formMethods,
        onSubmit,
        isPendingRequestApproval,
        isFormReadOnly,
      }}
    >
      {children}
    </CropPledgeDocumentationContext.Provider>
  );
};

export const useCropPledgeDocumentationContext =
  (): CropPledgeDocumentationContextType => {
    const context = useContext(CropPledgeDocumentationContext);
    if (context === undefined) {
      throw new Error(
        'useCropPledgeDocumentationContext must be used within a CropPledgeDocumentationProvider',
      );
    }
    return context;
  };
